import { FC, useMemo, useState } from 'react';

import { Button, Input } from '../components/Form';
import {
  useCancelSessionReportGenerationMutation,
  ReportGenerationAdminSessionRow,
  useGetReportGenerationSessionsQuery,
  useRerunSessionReportGenerationMutation,
  useRunSessionReportGenerationMutation,
} from '../api/api';

const PAGE_SIZE = 50;


function formatDateTime(value?: string | null): string {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}


function shorten(value?: string | null): string {
  if (!value) {
    return '—';
  }

  if (value.length <= 18) {
    return value;
  }

  return `${value.slice(0, 8)}...${value.slice(-6)}`;
}


function buildReportStatus(row: ReportGenerationAdminSessionRow): string {
  if (row.has_report) {
    return 'completed';
  }

  return row.request_status || 'not_requested';
}


function getStatusBadgeClasses(status?: string | null): string {
  switch (status) {
    case 'completed':
    case 'finalized':
      return 'bg-emerald-100 text-emerald-800 ring-emerald-200';
    case 'running':
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 ring-blue-200';
    case 'queued':
    case 'created':
      return 'bg-amber-100 text-amber-800 ring-amber-200';
    case 'failed':
      return 'bg-red-100 text-red-800 ring-red-200';
    case 'cancelled':
      return 'bg-slate-100 text-slate-700 ring-slate-200';
    case 'not_requested':
      return 'bg-gray-100 text-gray-700 ring-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200';
  }
}


function formatStatusLabel(status?: string | null): string {
  if (!status) {
    return '—';
  }

  return status.split('_').join(' ');
}


function StatusBadge({ status }: { status?: string | null }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium capitalize ring-1 ring-inset ${getStatusBadgeClasses(
        status,
      )}`}
    >
      {formatStatusLabel(status)}
    </span>
  );
}


export const ReportGeneration: FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowActionState, setRowActionState] = useState<Record<string, string>>({});
  const offset = (page - 1) * PAGE_SIZE;

  const { data, isLoading, isFetching, refetch } = useGetReportGenerationSessionsQuery({
    search: search || undefined,
    limit: PAGE_SIZE,
    offset,
  });
  const [runReportGeneration] = useRunSessionReportGenerationMutation();
  const [rerunReportGeneration] = useRerunSessionReportGenerationMutation();
  const [cancelReportGeneration] = useCancelSessionReportGenerationMutation();

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const fromItem = total === 0 ? 0 : offset + 1;
  const toItem = Math.min(offset + rows.length, total);

  const summary = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        const reportStatus = buildReportStatus(row);
        acc.total += 1;
        acc[reportStatus] = (acc[reportStatus] ?? 0) + 1;
        return acc;
      },
      { total: 0 } as Record<string, number>,
    );
  }, [rows]);

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSearch(searchInput.trim());
    setPage(1);
  };

  const withRowAction = async (
    sessionId: string,
    action: 'run' | 'rerun' | 'cancel',
  ) => {
    setRowActionState((prev) => ({ ...prev, [sessionId]: action }));

    try {
      if (action === 'run') {
        await runReportGeneration({ sessionId }).unwrap();
      } else if (action === 'rerun') {
        await rerunReportGeneration({ sessionId }).unwrap();
      } else {
        await cancelReportGeneration({ sessionId }).unwrap();
      }
      await refetch();
    } catch (error) {
      console.error(`Failed to ${action} report generation`, error);
      window.alert(`Failed to ${action} report generation for session ${sessionId}`);
    } finally {
      setRowActionState((prev) => {
        const next = { ...prev };
        delete next[sessionId];
        return next;
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Report Generation</h1>
          <p className="mt-1 text-sm text-gray-600">
            Session report statuses with manual run and rerun actions.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => refetch()}
          className="bg-gray-700 hover:bg-gray-800"
        >
          Refresh
        </Button>
      </div>

      <form onSubmit={handleSearchSubmit} className="flex gap-3">
        <div className="w-full max-w-md">
          <Input
            label="Search by session, project, organization or user id"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="samw-nsxz-xaur"
          />
        </div>
        <div className="pt-7">
          <Button type="submit">Search</Button>
        </div>
      </form>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">Total</div>
          <div className="mt-1 text-2xl font-semibold">{summary.total ?? 0}</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">Completed</div>
          <div className="mt-1 text-2xl font-semibold">{summary.completed ?? 0}</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">Queued</div>
          <div className="mt-1 text-2xl font-semibold">{summary.queued ?? 0}</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">Running</div>
          <div className="mt-1 text-2xl font-semibold">{summary.running ?? 0}</div>
        </div>
        <div className="rounded-lg bg-white p-4 shadow-sm">
          <div className="text-sm text-gray-500">Failed</div>
          <div className="mt-1 text-2xl font-semibold">{summary.failed ?? 0}</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Participant</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Scenario</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Organization</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Session Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Report Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Job Status</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Finalized</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Error</th>
              <th className="px-4 py-3 text-left font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            )}

            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                  No sessions found.
                </td>
              </tr>
            )}

            {rows.map((row) => {
              const actionState = rowActionState[row.session_id];
              const isRowBusy = Boolean(actionState) || isFetching;
              const reportStatus = buildReportStatus(row);
              const isSessionInProgress = row.session_status === 'in_progress';
              const isCancelable = row.request_status === 'queued' || row.request_status === 'running' || row.job_status === 'queued' || row.job_status === 'running';
              const action: 'run' | 'rerun' | 'cancel' = isCancelable
                ? 'cancel'
                : row.has_report
                  ? 'rerun'
                  : 'run';
              const actionLabel =
                actionState === 'cancel'
                  ? 'Cancelling...'
                  : action === 'cancel'
                    ? 'Cancel'
                    :
                actionState === 'run'
                  ? 'Generating...'
                  : actionState === 'rerun'
                    ? 'Regenerating...'
                    : row.has_report
                      ? 'Regenerate'
                      : 'Generate';
              const actionDisabled = isRowBusy || (action !== 'cancel' && isSessionInProgress);

              return (
                <tr key={row.session_id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{row.user_full_name || '—'}</div>
                    <div className="font-mono text-xs text-gray-500">{row.user_id || '—'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{row.project_title || '—'}</div>
                    <div className="font-mono text-xs text-gray-500">{row.project_id}</div>
                    <div className="font-mono text-xs text-gray-400">{row.session_id}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{shorten(row.organization_id)}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={row.session_status} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={reportStatus} />
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={row.job_status} />
                  </td>
                  <td className="px-4 py-3">
                    <div>{formatDateTime(row.finalized_at)}</div>
                    <div className="text-xs text-gray-500">{row.finalized_reason || '—'}</div>
                  </td>
                  <td className="max-w-xs px-4 py-3 text-xs text-red-600">
                    {row.error_message || '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      type="button"
                      onClick={() => withRowAction(row.session_id, action)}
                      disabled={actionDisabled}
                      className={`${
                        action === 'cancel'
                          ? 'bg-red-600 hover:bg-red-700'
                          : row.has_report
                          ? 'bg-amber-600 hover:bg-amber-700'
                          : 'bg-blue-600 hover:bg-blue-700'
                      } disabled:cursor-not-allowed disabled:bg-gray-400`}
                      title={action !== 'cancel' && isSessionInProgress ? 'Session is still in progress' : undefined}
                    >
                      {actionLabel}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between rounded-lg bg-white px-4 py-3 shadow-sm">
        <div className="text-sm text-gray-600">
          Showing {fromItem}–{toItem} of {total}
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-gray-600">
            Page {page} of {totalPages}
          </div>
          <Button
            type="button"
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page <= 1 || isFetching}
            className="bg-gray-700 hover:bg-gray-800"
          >
            Prev
          </Button>
          <Button
            type="button"
            onClick={() => setPage((prev) => (prev < totalPages ? prev + 1 : prev))}
            disabled={page >= totalPages || isFetching}
            className="bg-gray-700 hover:bg-gray-800"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};
